import React, { useState, useEffect, useRef } from "react";
import { uploadFileToS3, analyzeFoodWithGemini, addData, formatRecipe, getData_Any2Column } from "../service/APIService";
import { Modal, Button } from "react-bootstrap";
import { v4 as uuid } from "uuid";
import CalorieHistoryComponent from "./CalorieHistoryComponent";
import NutritionComponent from "./NutritionComponent";
import DietJourneyGraph from "./DietJourneyGraph";
import secureLocalStorage from "react-secure-storage";
import config from "../config.json";
import Resizer from "react-image-file-resizer";
import { useSubscription } from "../context/SubscriptionContext";
const ImageUpload = ({ isEmbedded = false }) => {
  const { isSubscribed, recipeCount: contextRecipeCount, incrementScanCount } = useSubscription();
  // Pro users get the full daily limit; non-pro (free) users get the smaller limit.
  const dailyLimit = isSubscribed ? config["pro-dailyLimit"] : config["non-pro-dailyLimit"];
  const [file, setFile] = useState(null); // Stores the uploaded file
  const [imageUrl, setImageUrl] = useState(""); // Stores the S3 URL

  // Safe preview URL generator (guards against non-Blob values)
  const getPreviewUrl = () => {
    if (imageUrl) return imageUrl;
    if (file && (file instanceof Blob || file instanceof File)) {
      try {
        return URL.createObjectURL(file);
      } catch (e) {
        return "";
      }
    }
    return "";
  };
  const [loading, setLoading] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [totalScans, setTotalScans] = useState(0);
  const [dailyCount, setDailyCount] = useState(0);
  const [error, setError] = useState("");
  const [caption, setCaption] = useState("");
  const [uploaded, setUploaded] = useState(false);
  const [nutritionInfo, setNutritionInfo] = useState({ title: "", calories: "", carbs: "", protein: "", fat: "" });
  const [remainingUploads, setRemainingUploads] = useState(0);
  const [calorieHistory, setCalorieHistory] = useState([]);
  const [isGuest, setIsGuest] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [scanStepIndex, setScanStepIndex] = useState(0);
  const scanSteps = [
    "Detecting food contours...",
    "Analyzing ingredient textures...",
    "Estimating portion & volume...",
    "Calculating calories & macros...",
    "Finalizing nutritional breakdown...",
  ];

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setScanStepIndex((prev) => (prev + 1) % scanSteps.length);
      }, 1600);
    } else {
      setScanStepIndex(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const loggedInUser = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
  const userKey = loggedInUser?.email || "guest";

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isAndroidWebView = /Android.*(wv|Version\/[\d.]+).*Chrome/.test(userAgent);

  console.log(isAndroidWebView ? "Running in Android WebView" : "Not in WebView");
  // Returns today's date in YYYY-MM-DD using LOCAL timezone (not UTC)
  const getLocalToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchRemainingScans = async () => {
      setCheckingLimit(true);
      const is_guest = !loggedInUser;
      setIsGuest(is_guest);
      const today = getLocalToday();

      if (is_guest) {
        const currentGuestCount = contextRecipeCount;
        setTotalScans(currentGuestCount);
        setDailyCount(currentGuestCount); // Shared lifetime limit for guests
        setRemainingUploads(Math.max(0, config.guestCount - currentGuestCount));
        setCheckingLimit(false);
        return;
      }

      try {
        const data = await getData_Any2Column("email", userKey, "type", "recipe-ai-food-calorie");
        const count = data.length;
        setTotalScans(count);
        setCalorieHistory(data || []);

        // Filter for today's scans — logged-in users are limited per-day.
        // Track consumed quota so deleting a record does NOT refund/restore daily quota.
        const storedUploadData = JSON.parse(secureLocalStorage.getItem("UploadrecipeData")) || {};
        const recordedTodayUsage = storedUploadData[today]?.[userKey] || 0;

        const todayScans = data.filter(item => item.date && item.date.startsWith(today));
        const effectiveTodayCount = Math.max(todayScans.length, recordedTodayUsage);

        if (effectiveTodayCount > recordedTodayUsage) {
          if (!storedUploadData[today]) storedUploadData[today] = {};
          storedUploadData[today][userKey] = effectiveTodayCount;
          secureLocalStorage.setItem("UploadrecipeData", JSON.stringify(storedUploadData));
        }

        setDailyCount(effectiveTodayCount);
        setRemainingUploads(Math.max(0, dailyLimit - effectiveTodayCount));
      } catch (err) {
        console.error("Error fetching calorie history count:", err);
        setRemainingUploads(0);
      } finally {
        setCheckingLimit(false);
      }
    };

    fetchRemainingScans();
  }, [userKey, isSubscribed, contextRecipeCount]);

  const resizeImage = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        512, // max width
        512, // max height
        "JPG", // output format
        80, // quality
        0, // rotation
        (uri) => resolve(uri), // callback with resized image as URI
        "file" // output type
      );
    });
  const handleFileChange = async (e) => {
    handleUploadAgain();
    const uploadedFile = e.target.files[0];
    if (uploadedFile?.size > 5000000) {
      alert("Please upload a file smaller than 5MB.");
      return;
    }
    try {
      console.log("image size before");
      console.log(uploadedFile?.size);
      const resizedImage = await resizeImage(uploadedFile);
      console.log("image size after");
      console.log(resizedImage?.size);
      setFile(resizedImage);
      setError("");
      return;
    } catch (err) {
      console.error("Error resizing image:", err);
      setError("Failed to process the image. Please try again.");
    }
  };

  const extractNutritionInfo = (caption) => {
    const sanitizeValue = (value) => {
      return value
        .replace(/Approximately/gi, "")
        .replace(/calories/gi, "")
        .replace(/grams?\b/gi, "g")
        .trim();
    };

    if (caption.toLowerCase().includes("not a food image")) {
      return { title: "❌ Not Food", calories: "N/A", carbs: "N/A", protein: "N/A", fat: "N/A" };
    }

    const titleMatch = caption.match(/\*\*Food Name:\*\* (.*?)(?:\n|$)/);
    const caloriesMatch = caption.match(/\*\*Calories:\*\* (.*?)(?:\n|$)/);
    const carbsMatch = caption.match(/\*\*Carbohydrates:\*\* (.*?)(?:\n|$)/);
    const sugarMatch = caption.match(/\*\*Sugar:\*\* (.*?)(?:\n|$)/) || caption.match(/sugar[:\s]+(\d+g?)/i);
    const proteinMatch = caption.match(/\*\*Protein:\*\* (.*?)(?:\n|$)/);
    const fatMatch = caption.match(/\*\*Fat:\*\* (.*?)(?:\n|$)/);

    return {
      title: titleMatch ? sanitizeValue(titleMatch[1]) : "Unknown",
      calories: caloriesMatch ? sanitizeValue(caloriesMatch[1]) : "N/A",
      carbs: carbsMatch ? sanitizeValue(carbsMatch[1]) : "N/A",
      sugar: sugarMatch ? sanitizeValue(sugarMatch[1]) : "",
      protein: proteinMatch ? sanitizeValue(proteinMatch[1]) : "N/A",
      fat: fatMatch ? sanitizeValue(fatMatch[1]) : "N/A",
    };
  };
  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    setLoading(true);
    setError("");
    const imagename = `${uuid()}-uploaded-image.jpg`; // Unique file name
    var url;
    try {
      url = await uploadFileToS3(imagename, file);
      setImageUrl(url); // Set the S3 URL to display the image

      // New Gemini Vision Analysis
      const generatedCaption = await analyzeFoodWithGemini(file, userKey);

      const nutritionData = extractNutritionInfo(generatedCaption);
      setNutritionInfo(nutritionData);
      setCaption(generatedCaption);

      // Only save to DB if it's actually food
      if (nutritionData.title !== "❌ Not Food") {
        await saveDataToDB(nutritionData, url, generatedCaption);
      }
      setError(nutritionData.title === "❌ Not Food" ? nutritionData.title : "");

      if (isGuest) {
        incrementScanCount();
      } else {
        // Update daily uploads in local storage
        const today = getLocalToday();
        const recipeData = JSON.parse(secureLocalStorage.getItem("UploadrecipeData")) || {};

        if (!recipeData[today]) {
          recipeData[today] = {};
        }
        recipeData[today][userKey] = (recipeData[today][userKey] || 0) + 1;

        secureLocalStorage.setItem("UploadrecipeData", JSON.stringify(recipeData));
      }

      setDailyCount((prev) => prev + 1);
      setTotalScans((prev) => prev + 1);
      setRemainingUploads((prev) => prev - 1); // Update remaining uploads
      setUploaded(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      // We no longer clear the file[] here so the user can retry
      setCaption("");
      setNutritionInfo({ title: "Image", calories: "", carbs: "", protein: "", fat: "" });
      setUploaded(false);
      setError(err.message || "Failed to get the image information. Please try again.");
      //await saveDataToDB(nutritionInfo, url, error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAgain = () => {
    setFile(null);
    setImageUrl("");
    setCaption("");
    setError("");
    setUploaded(false); // Reset upload status
    setNutritionInfo({ title: "", calories: "", carbs: "", protein: "", fat: "" });
  };

  const saveDataToDB = async ({ title, calories, carbs, protein, fat }, imageUrl, generatedCaption) => {
    const addRecipe = {
      id: uuid(), // Unique ID for the data
      title,
      calories,
      carbs,
      protein,
      fat,
      recipeText: generatedCaption,
      image: imageUrl,
      type: "recipe-ai-food-calorie",
      email: userKey,
      ainame: "gemini",
      date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`; })(), // Local timestamp
    };

    try {
      await addData(addRecipe);
      console.log("Data saved successfully:", addRecipe);
      setCalorieHistory((prev) => [addRecipe, ...prev]);
    } catch (error) {
      console.error("Error saving data to DB:", error);
      setError("Failed to save data to the database.");
    }
  };
  const [facingMode, setFacingMode] = useState("environment");

  const handleOpenCamera = async () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        if (cameraStream) stopCamera();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facingMode },
        });
        setCameraStream(stream);
        setShowCameraModal(true);
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        }, 300);
      } catch (err) {
        console.error("Error accessing camera:", err);
        alert("Camera access is not allowed or not supported. Please check permissions.");
      }
    } else {
      alert("Camera not supported on this browser.");
    }
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  // Effect to restart camera when facingMode changes while modal is open
  useEffect(() => {
    if (showCameraModal) {
      handleOpenCamera();
    }
  }, [facingMode]);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(async (blob) => {
        if (blob) {
          const capturedFile = new File([blob], "captured-photo.jpg", { type: "image/jpeg" });
          await handleFileChange({ target: { files: [capturedFile] } });
          stopCamera();
        }
      }, "image/jpeg", 0.9);
    }
  };

  return (
    <div className={`container ${isEmbedded ? "px-0" : "py-2 mb-3 pb-2"}`}>
      {/* ── 1. SCAN RESULT VIEW (Matches media_1793021082376.png) ── */}
      {uploaded && !loading && imageUrl ? (
        <div className="scan-result-container">
          {/* Top Hero Food Image with Back/Retake Button */}
          <div className="scan-result-hero-wrap">
            <img
              src={getPreviewUrl()}
              alt={nutritionInfo?.title || "Scanned Food"}
              className="scan-result-hero-img"
            />
            <button
              type="button"
              className="scan-result-back-btn"
              onClick={handleUploadAgain}
              title="Scan another food"
              aria-label="Scan another food"
            >
              <i className="fi fi-rr-arrow-left"></i>
            </button>
          </div>

          {/* Overlaid Curved Sheet */}
          <div className="scan-result-sheet">
            {/* Centered Nutrition Subtitle & Dish Title */}
            <div className="text-center mb-3">
              <span className="nutrition-pre-heading">Nutrition</span>
              <h2 className="nutrition-food-heading">
                {nutritionInfo?.title ? nutritionInfo.title.replace(/"/g, "").replace(/\*\*/g, "").trim() : "Food Item"}
              </h2>
            </div>

            {/* NutritionComponent with circular ring + 3 striped meters */}
            <NutritionComponent nutritionInfo={nutritionInfo} />

            {/* "Last scans" Section with Carousel */}
            <CalorieHistoryComponent variant="carousel" showLatest={true} />

            {/* Actions: Scan Another Food + Vision Notes */}
            <div className="d-flex flex-column gap-3 mt-4 pt-1">
              <button
                type="button"
                className="btn py-3 px-4 rounded-pill fw-bold text-white shadow-sm d-flex align-items-center justify-content-center gap-2"
                onClick={handleUploadAgain}
                style={{
                  background: "#0f1f14",
                  border: "1px solid rgba(34, 197, 94, 0.35)",
                  fontSize: "0.95rem",
                  boxShadow: "0 6px 18px rgba(15, 31, 20, 0.35)"
                }}
              >
                <i className="fi fi-rr-camera" style={{ fontSize: "1.1rem" }}></i>
                <span>Scan Another Food</span>
              </button>

              {caption && (
                <div className="mt-1">
                  <details className="scan-details-collapsible">
                    <summary className="scan-details-summary">
                      <span className="d-flex align-items-center gap-1.5">
                        <i className="fi fi-rr-document-signed text-success"></i>
                        <span>AI Vision Recipe & Nutrition Details</span>
                      </span>
                      <i className="fi fi-rr-angle-small-down"></i>
                    </summary>
                    <div className="scan-details-content">
                      {formatRecipe(caption)}
                    </div>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── 2. PRE-SCAN / SCANNER WORKFLOW (Idle or Captured/Analyzing) ── */
        <>
          {!isEmbedded && (
            <div className="text-center mb-3">
              <span className="badge rounded-pill px-3 py-1 bg-success bg-opacity-10 border border-success border-opacity-25 text-success fw-bold" style={{ fontSize: "11px" }}>
                🔥 AI Food Vision & Calories
              </span>
              <h4 className="fw-bold text-dark mt-2 mb-1" style={{ fontSize: "1.35rem", letterSpacing: "-0.3px" }}>
                Food Calorie Scanner
              </h4>
              <p className="text-secondary small mb-0" style={{ maxWidth: "440px", margin: "0 auto", fontSize: "12.5px" }}>
                Scan food using your camera or upload a food photo to detect meals and calculate calories & macros.
              </p>
            </div>
          )}

          {/* Conditional: Idle Launch Card vs. Captured Frame */}
          {!imageUrl && !file && !loading ? (
            <div className="calorie-launch-card">
              <div className="calorie-launch-icon-wrap">
                <i className="fi fi-rr-camera text-success"></i>
              </div>
              <h4 className="fw-bolder text-dark mb-1" style={{ fontSize: "1.3rem", letterSpacing: "-0.3px" }}>
                Scan Food for Calories
              </h4>
              <p className="text-secondary small mb-4" style={{ maxWidth: "340px", margin: "0 auto", fontSize: "13px", lineHeight: "1.5" }}>
                Point your camera at any meal or pick a photo from your gallery to detect food and calculate calories & macros.
              </p>

              <div className="d-flex flex-column w-100" style={{ maxWidth: "340px", margin: "0 auto", gap: "14px" }}>
                <button
                  type="button"
                  className="btn btn-success py-3 px-4 rounded-pill fw-bold text-white shadow-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={handleOpenCamera}
                  style={{
                    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    boxShadow: "0 6px 20px rgba(34, 197, 94, 0.32)",
                    fontSize: "0.95rem"
                  }}
                >
                  <i className="fi fi-rr-camera" style={{ fontSize: "1.15rem" }}></i>
                  <span>Open Camera & Snap Food</span>
                </button>

                <button
                  type="button"
                  className="btn py-2.5 px-4 rounded-pill fw-bold text-dark d-flex align-items-center justify-content-center gap-2 mt-1"
                  onClick={() => document.getElementById("fileUpload")?.click()}
                  style={{
                    background: "#ffffff",
                    border: "1.5px solid #e2e8f0",
                    fontSize: "0.90rem",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.03)"
                  }}
                >
                  <i className="fi fi-rr-picture text-success" style={{ fontSize: "1.05rem" }}></i>
                  <span>Choose from Gallery</span>
                </button>
              </div>

              {/* Hidden file input */}
              <input
                type="file"
                id="fileUpload"
                className="d-none"
                onChange={handleFileChange}
                disabled={loading || remainingUploads === 0}
                accept="image/*"
              />
            </div>
          ) : (
            /* Viewfinder Viewport (Opened After Taking/Selecting Image) */
            <div className="scanner-container">
              <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25 fw-bold" style={{ fontSize: "11px" }}>
                  📸 Captured Food in Frame
                </span>
                {!loading && !uploaded && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none text-danger p-0 fw-semibold"
                    style={{ fontSize: "12px" }}
                    onClick={handleUploadAgain}
                  >
                    <i className="fi fi-rr-refresh me-1"></i> Retake
                  </button>
                )}
              </div>

              <div className="scanner-viewfinder-card">
                {/* Green corner brackets */}
                <div className="scanner-corner top-left"></div>
                <div className="scanner-corner top-right"></div>
                <div className="scanner-corner bottom-left"></div>
                <div className="scanner-corner bottom-right"></div>

                {/* Photo preview */}
                <div style={{ position: "relative", width: "100%", height: "100%" }}>
                  <img
                    src={getPreviewUrl()}
                    alt="Food preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {!uploaded && !loading && (
                    <button
                      type="button"
                      onClick={handleUploadAgain}
                      className="scanner-remove-btn"
                      title="Remove photo"
                      aria-label="Remove photo"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                {/* High-Tech AI Food Vision Scanner HUD Overlay */}
                {loading && (
                  <div className="scanner-hud-overlay">
                    <div className="scanner-grid-bg"></div>
                    <div className="scanner-laser-sweep"></div>

                    {/* Radar wave pulses */}
                    <div className="radar-wave"></div>
                    <div className="radar-wave"></div>
                    <div className="radar-wave"></div>

                    {/* Floating Detected Nutrition Badges */}
                    <div className="detected-node-badge node-calories">
                      <span>🔥</span> Calories: Analyzing...
                    </div>
                    <div className="detected-node-badge node-protein">
                      <span>🥩</span> Protein: Estimating...
                    </div>
                    <div className="detected-node-badge node-carbs">
                      <span>🌾</span> Carbs: Measuring...
                    </div>
                    <div className="detected-node-badge node-fats">
                      <span>🥑</span> Fats: Calculating...
                    </div>

                    {/* Central Target HUD Reticle */}
                    <div className="hud-reticle-wrap">
                      <div className="hud-ring-outer"></div>
                      <div className="hud-ring-inner"></div>
                      <div className="hud-center-dot"></div>
                    </div>

                    {/* Vision Scan Step Ticker */}
                    <div className="mt-3 text-center position-relative" style={{ zIndex: 10 }}>
                      <span className="badge rounded-pill bg-success bg-opacity-25 border border-success text-white px-3 py-1 fw-bold mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                        AI Vision Food Scanner
                      </span>
                      <p className="fw-bold text-white mb-0" style={{ fontSize: "0.95rem" }}>
                        {scanSteps[scanStepIndex]}
                      </p>
                      <small className="text-white-50" style={{ fontSize: "0.72rem" }}>
                        Recognizing ingredients & macro breakdown
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {/* Hidden file input */}
              <input
                type="file"
                id="fileUpload"
                className="d-none"
                onChange={handleFileChange}
                disabled={loading || remainingUploads === 0}
                accept="image/*"
              />

              {/* Action buttons under captured frame */}
              {!uploaded ? (
                <div className="d-flex gap-2 my-3">
                  <button
                    type="button"
                    className="btn btn-light border rounded-pill px-3 py-2.5 text-secondary fw-semibold d-inline-flex align-items-center justify-content-center gap-1.5"
                    onClick={handleUploadAgain}
                    disabled={loading}
                  >
                    <i className="fi fi-rr-cross-small"></i> Change Photo
                  </button>
                  <button
                    type="button"
                    className="btn btn-success flex-grow-1 py-2.5 rounded-pill text-white fw-bold shadow-sm d-inline-flex align-items-center justify-content-center gap-2"
                    onClick={handleUpload}
                    disabled={loading || remainingUploads === 0 || checkingLimit}
                    style={{
                      background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                      boxShadow: "0 6px 20px rgba(34, 197, 94, 0.35)",
                    }}
                  >
                    <i className={`fas ${loading ? "fa-spinner fa-spin" : "fa-fire"}`}></i>
                    {loading ? "Calculating..." : "Calculate Calories & Macros"}
                  </button>
                </div>
              ) : (
                <div className="text-center my-3">
                  <button
                    type="button"
                    className="btn btn-outline-success rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2"
                    onClick={handleUploadAgain}
                  >
                    <i className="fi fi-rr-refresh"></i> Scan Another Food
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Animated Diet Journey Graph (Below Scanner Section) */}
          <DietJourneyGraph calorieHistory={calorieHistory} />

          {/* Theme-Friendly Scan Quota Card */}
          <div className="app-quota-card">
            <div className="quota-card-header">
              <div className="d-flex align-items-center gap-3">
                <div className={`quota-icon-wrap ${remainingUploads === 0 ? "exhausted" : ""}`}>
                  <i className={`fas ${remainingUploads === 0 ? "fa-exclamation-circle" : "fa-calendar-check"}`}></i>
                </div>
                <div>
                  <h6 className="quota-title">
                    {isGuest ? "Free Scan Quota" : "Daily Food Scan Quota"}
                  </h6>
                  <div className="quota-subtitle">
                    {isSubscribed ? (
                      <span className="text-success fw-semibold">
                        <i className="fas fa-sparkles me-1"></i> Pro Subscription Active
                      </span>
                    ) : (
                      <span>Free Tier Access</span>
                    )}
                  </div>
                </div>
              </div>
              <span className={`quota-pill-badge ${remainingUploads === 0 ? "exhausted" : ""}`}>
                {isGuest
                  ? `${totalScans} / ${config.guestCount}`
                  : `${Math.max(0, dailyCount)} / ${dailyLimit}`}
              </span>
            </div>

            <div className="quota-progress-track">
              <div
                className={`quota-progress-bar ${remainingUploads === 0 ? "exhausted" : ""}`}
                style={{
                  width: `${Math.min(100, (isGuest
                    ? (totalScans / config.guestCount)
                    : (dailyCount / dailyLimit)) * 100)}%`
                }}
              ></div>
            </div>

            <div className="quota-stats-row">
              <span className="quota-stat-item">
                <i className="fi fi-rr-check-circle text-success"></i>
                <span>{isGuest ? "Total Scans" : "Today's Scans"}:</span>
                <strong>{isGuest ? totalScans : dailyCount}</strong>
              </span>
              <span className="quota-stat-item">
                <span>Remaining:</span>
                <span className={`remaining-highlight ${remainingUploads === 0 ? "zero" : ""}`}>
                  {remainingUploads} {remainingUploads === 1 ? "scan" : "scans"}
                </span>
              </span>
            </div>

            {!isGuest && remainingUploads === 0 && (
              <div className="quota-exhausted-alert">
                <i className="fi fi-rr-clock"></i>
                <span>Daily limit reached — resets at 12:00 AM midnight</span>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center shadow-sm">
              <div className="flex-grow-1 mb-0 small">
                <i className="fi fi-rr-info-bubble me-2"></i>
                {error}
              </div>
              <button className="btn btn-sm btn-outline-danger ms-3 text-nowrap" onClick={handleUpload}>
                <i className="fi fi-rr-refresh me-1"></i> Retry
              </button>
            </div>
          )}

          {/* Calorie History list */}
          <div className="mt-3">
            <CalorieHistoryComponent showLatest={true} />
          </div>
        </>
      )}

      {/* Camera Live Preview Modal */}
      <Modal show={showCameraModal} onHide={stopCamera} centered size="lg">
        <Modal.Header closeButton className="bg-light d-flex justify-content-between align-items-center">
          <Modal.Title className="h6 fw-bold mb-0">Scan Your Food </Modal.Title>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-4 rounded-pill px-3"
            onClick={toggleCamera}
          >
            <i className="fas fa-sync shadow-sm"></i> Flip
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0 position-relative bg-dark" style={{ minHeight: "300px" }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", height: "auto", display: "block" }}
          />
          {/* Alignment Corner Brackets Overlay on live camera stream */}
          <div className="camera-frame-overlay">
            <div className="camera-align-guide-badge">
              <i className="fas fa-crosshairs me-1 text-success"></i> Align Food in Frame
            </div>
            <div className="scanner-corner top-left"></div>
            <div className="scanner-corner top-right"></div>
            <div className="scanner-corner bottom-left"></div>
            <div className="scanner-corner bottom-right"></div>
          </div>
          <div
            className="position-absolute bottom-0 start-50 translate-middle-x mb-4 w-100 text-center"
            style={{ zIndex: 10 }}
          >
            <button
              type="button"
              className="camera-shutter-btn"
              onClick={capturePhoto}
              aria-label="Capture Food Photo"
            >
              <div className="camera-shutter-inner"></div>
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-0">
          <Button variant="outline-light" size="sm" onClick={stopCamera}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default ImageUpload;
